import React from 'react';

import { useNavigate } from 'react-router-dom';

import {
    AssetDetails,
    Button,
    Modal,
    ModalCloseButtonPositions,
    ModalPaddingSizes,
    ModalVariants,
} from '@pkg/components';
import { ModalHeaderAlignments } from '@pkg/components/Modal/components';
import { useAppSelector } from '@pkg/hooks';
import { type MarketplaceAsset } from '@pkg/types';

import { getNftDetailsUrl } from '@app/helpers';

import {
    ActionLink,
    Actions,
    ActionTile,
    ActionTitle,
    Summary,
    SummaryCount,
    SummaryHeader,
    TitleLarge,
    TitleMedium,
} from './AssetListingModal.style';

export interface AssetListingModalProps {
    asset: MarketplaceAsset;
    type?: 'preListing' | 'postListing';
    onClose: () => void;
}

// TODO: Migrate strings to `i18n`.
export function AssetListingModal({ asset, type = 'preListing', onClose }: AssetListingModalProps) {
    const navigate = useNavigate();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);

    function handleViewAsset() {
        onClose();

        const nftDetailsUrl = getNftDetailsUrl({
            contractAddress: asset.collection.contractAddress,
            tokenId: asset.tokenId,
        });

        navigate(nftDetailsUrl);
    }

    function handleViewProfile() {
        onClose();
        const profilePath = primaryYat ? `${primaryYat}` : 'profile';
        navigate(`/${profilePath}`);
    }

    const preListingMarkup = (
        <>
            <Summary>
                <SummaryHeader>
                    <TitleMedium>Summary</TitleMedium>
                    <SummaryCount>1 Item</SummaryCount>
                </SummaryHeader>

                <AssetDetails
                    key={asset?.name}
                    name={asset?.name ?? ''}
                    collectionName={asset?.collection.name}
                    imageUrl={asset?.imageUrl}
                />
            </Summary>

            <ActionTile>
                <ActionTitle>Go to your wallet</ActionTitle>
                <div>You&apos;ll be asked to approve this purchase from your wallet.</div>
            </ActionTile>
        </>
    );

    const postListingMarkup = (
        <>
            <ActionTile key={asset?.name}>
                <AssetDetails
                    name={asset?.name ?? ''}
                    collectionName={asset?.collection.name}
                    imageUrl={asset?.imageUrl}
                    size="large"
                />
            </ActionTile>

            <Actions>
                <Button size="large" onClick={handleViewAsset}>
                    View your asset
                </Button>
                <ActionLink onClick={handleViewProfile}>View on profile</ActionLink>
            </Actions>
        </>
    );

    return (
        <Modal
            variant={ModalVariants.Simple}
            closeButtonPosition={ModalCloseButtonPositions.Header}
            padded
            paddingSize={ModalPaddingSizes.Large}
            onClose={onClose}
        >
            <Modal.Header align={ModalHeaderAlignments.Left}>
                <TitleLarge>{type === 'postListing' ? 'Your Listing is Complete' : 'Approve Listing'}</TitleLarge>
            </Modal.Header>

            <Modal.Layout>
                <Modal.Layout.Section>
                    {type === 'postListing' ? postListingMarkup : preListingMarkup}
                </Modal.Layout.Section>
            </Modal.Layout>
        </Modal>
    );
}
